import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BsEye, BsChevronDown, BsChevronUp, BsBuilding, BsPeople, BsInboxFill } from 'react-icons/bs';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import PageHeader from '../../components/PageHeader/PageHeader';
import FilterCard from '../../components/FilterCard/FilterCard';
import styles from './YayinlarBirimler.module.css';

const NoDataFound = () => (
  <div className="no-data-found">
    <BsInboxFill className="no-data-icon" />
    <h3 className="no-data-title">Gösterilecek Veri Bulunamadı</h3>
    <p className="no-data-text">
      Arama kriterlerinize uygun veri bulunmamaktadır. Lütfen filtrelerinizi değiştirerek tekrar deneyin.
    </p>
  </div>
);

const chartOptions = {
  chart: {
    type: 'column',
    height: 250,
    backgroundColor: 'transparent'
  },
  title: {
    text: null
  },
  xAxis: {
    categories: ['Q1', 'Q2'],
    crosshair: true
  },
  yAxis: {
    min: 0,
    title: {
      text: 'Yayın Sayısı'
    }
  },
  tooltip: {
    headerFormat: '<span style="font-size:10px">{point.key}</span><table>',
    pointFormat: '<tr><td style="color:{series.color};padding:0">{series.name}: </td>' +
      '<td style="padding:0"><b>{point.y}</b></td></tr>',
    footerFormat: '</table>',
    shared: true,
    useHTML: true
  },
  plotOptions: {
    column: {
      pointPadding: 0.2,
      borderWidth: 0
    }
  },
  credits: {
    enabled: false
  },
  series: []
};

const birimler = [
  {
    id: 1,
    birim: "Uçak Mühendisliği",
    fakulte: "Mühendislik Fakültesi",
    detaylar: {
      ogretimUyesiSayisi: 20,
      makaleReviewSayisi: 30,
      ogretimUyesiBasinaQ1Q2: 1.40,
      ogretimUyesiBasinaMRSayisi: 1.50,
      ogretimUyesiBasinaQ1: 0.85,
      ogretimUyesiBasinaQ2: 0.55,
      q1Sayisi: 17,
      q1Yuzdesi: 56.7,
      q2Sayisi: 11,
      q2Yuzdesi: 36.7,
      q1q2Toplam: 28,
      q1q2Yuzdesi: 93.3,
      makaleVeReviewSayisi: 30,
      q1YuzdeGrafik: 56.7,
      q2YuzdeGrafik: 36.7
    },
    akademisyenler: [
      {
        id: 1,
        unvan: "Prof. Dr.",
        adSoyad: "Süleyman Türkmen",
        yayinlar: {
          toplam: 4,
          q1: 0,
          q2: 0,
          atifSayisi: 4,
          diger: 0,
          toplamAtif: 4,
          hIndex: 18
        }
      }
    ]
  },
];

const YayinlarBirimler = () => {
  const navigate = useNavigate();
  const [selectedFilters, setSelectedFilters] = useState({
    yil: '2024',
    donem: '',
  });
  const [expandedRows, setExpandedRows] = useState(() => new Set(birimler.map(b => b.id)));
  const [searchTerm, setSearchTerm] = useState('');

  const toggleRow = (id) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleBirimDetay = (birimId) => {
    const selectedBirim = birimler.find(b => b.id === birimId);
    if (selectedBirim) {
      navigate(`/birim/${birimId}`);
    }
  };

  const handleAkademisyenlerGit = (birimId) => {
    const selectedBirim = birimler.find(b => b.id === birimId);
    if (selectedBirim) {
      navigate(`/akademisyenler?birim=${selectedBirim.id}&birimAd=${encodeURIComponent(selectedBirim.birim)}&fakulte=${encodeURIComponent(selectedBirim.fakulte)}`);
    }
  };

  const handleAkademisyenDetay = (akademisyenId) => {
    navigate(`/akademisyen/${akademisyenId}`);
  };

  const filteredBirimler = birimler.filter(birim => 
    birim.birim.toLowerCase().includes(searchTerm.toLowerCase()) ||
    birim.fakulte.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderTableContent = () => {
    if (filteredBirimler.length === 0) {
      return <NoDataFound />;
    }

    return (
      <div className={styles.tableResponsive}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th style={{width: "40px"}}></th>
              <th>Birim</th>
              <th>Fakülte</th>
              <th style={{width: "120px"}}>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {filteredBirimler.map(birim => (
              <React.Fragment key={birim.id}>
                <tr className={styles.mainRow}>
                  <td>
                    <button 
                      className={styles.toggleButton}
                      onClick={() => toggleRow(birim.id)}
                      aria-label={expandedRows.has(birim.id) ? "Detayları gizle" : "Detayları göster"}
                    >
                      {expandedRows.has(birim.id) ? <BsChevronUp /> : <BsChevronDown />}
                    </button>
                  </td>
                  <td>{birim.birim}</td>
                  <td>{birim.fakulte}</td>
                  <td>
                    <div className={styles.actionButtons}>
                      <button 
                        className={`${styles.actionButton} ${styles.view}`}
                        onClick={() => handleBirimDetay(birim.id)}
                        title="Birim Detayı"
                      >
                        <BsBuilding />
                      </button>
                      <button 
                        className={`${styles.actionButton} ${styles.users}`}
                        onClick={() => handleAkademisyenlerGit(birim.id)}
                        title="Akademisyenler"
                      >
                        <BsPeople />
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedRows.has(birim.id) && (
                  <tr>
                    <td colSpan="4">
                      <div className={styles.expandedContent}>
                        <div className={styles.statsSection}>
                          <div className={styles.statsGrid}>
                            <div className={styles.statCard}>
                              <div className={styles.statHeader}>Öğretim Üyesi Sayısı</div>
                              <div className={styles.statValue}>{birim.detaylar.ogretimUyesiSayisi}</div>
                            </div>
                            <div className={styles.statCard}>
                              <div className={styles.statHeader}>Öğretim Üyesi Başına Q1 + Q2 (2024)</div>
                              <div className={styles.statValue}>{birim.detaylar.makaleVeReviewSayisi}</div>
                            </div>
                            <div className={styles.statCard}>
                              <div className={styles.statHeader}>Öğretim Üyesi Başına Q1 (2024)</div>
                              <div className={styles.statValue}>{birim.detaylar.ogretimUyesiBasinaQ1Q2.toFixed(2)}</div>
                            </div>
                          </div>
                          <div className={styles.statsGrid}>
                            <div className={styles.statCard}>
                              <div className={styles.statHeader}>Q1 (2024)ı</div>
                              <div className={styles.statValue}>{birim.detaylar.ogretimUyesiSayisi}</div>
                            </div>
                            <div className={styles.statCard}>
                              <div className={styles.statHeader}>Q2 (2024)</div>
                              <div className={styles.statValue}>{birim.detaylar.makaleVeReviewSayisi}</div>
                            </div>
                            <div className={styles.statCard}>
                              <div className={styles.statHeader}>Q1 + Q2 (2024)</div>
                              <div className={styles.statValue}>{birim.detaylar.ogretimUyesiBasinaQ1Q2.toFixed(2)}</div>
                            </div>
                          </div>
                          <div className={styles.statsGrid}>
                            <div className={styles.statCard}>
                              <div className={styles.statHeader}>Makale ve Review Sayısı (2024) </div>
                              <div className={styles.statValue}>{birim.detaylar.ogretimUyesiSayisi}</div>
                            </div>
                            <div className={styles.statCard}>
                              <div className={styles.statHeader}>Öğretim Üyesi Başına M-R Sayısı (2024)</div>
                              <div className={styles.statValue}>{birim.detaylar.makaleVeReviewSayisi}</div>
                            </div>
                            <div className={styles.statCard}>
                              <div className={styles.statHeader}>Öğretim Üyesi Başına Q2 (2024)</div>
                              <div className={styles.statValue}>{birim.detaylar.ogretimUyesiBasinaQ1Q2.toFixed(2)}</div>
                            </div>
                          </div>
                          <div className={styles.statsGrid}>
                            <div className={styles.statCard}>
                              <div className={styles.statHeader}>Q1 Yüzdesi </div>
                              <div className={styles.statValue}>{birim.detaylar.ogretimUyesiSayisi}</div>
                            </div>
                            <div className={styles.statCard}>
                              <div className={styles.statHeader}>Q2 Yüzdesi</div>
                              <div className={styles.statValue}>{birim.detaylar.makaleVeReviewSayisi}</div>
                            </div>
                            <div className={styles.statCard}>
                              <div className={styles.statHeader}>Q1 + Q2 Yüzdesi</div>
                              <div className={styles.statValue}>{birim.detaylar.ogretimUyesiBasinaQ1Q2.toFixed(2)}</div>
                            </div>
                          </div>

                          <div className={styles.chartSection}>
                            <div className={styles.chartContainer}>
                              <HighchartsReact
                                highcharts={Highcharts}
                                options={{
                                  ...chartOptions,
                                  series: [{
                                    name: 'Yayın Sayısı',
                                    data: [
                                      {y: birim.detaylar.q1Sayisi, color: '#4CAF50'},
                                      {y: birim.detaylar.q2Sayisi, color: '#2196F3'}
                                    ]
                                  }]
                                }}
                              />
                            </div>
                            <div className={styles.chartStats}>
                              <div className={styles.chartStat}>
                                <span className={styles.statLabel}>Q1 Yayın</span>
                                <div className={styles.statProgress}>
                                  <div 
                                    className={styles.progressFill} 
                                    style={{
                                      width: `${birim.detaylar.q1Yuzdesi}%`,
                                      backgroundColor: '#4CAF50'
                                    }}
                                  />
                                </div>
                                <span className={styles.statPercentage}>{birim.detaylar.q1Yuzdesi}%</span>
                              </div>
                              <div className={styles.chartStat}>
                                <span className={styles.statLabel}>Q2 Yayın</span>
                                <div className={styles.statProgress}>
                                  <div 
                                    className={styles.progressFill} 
                                    style={{
                                      width: `${birim.detaylar.q2Yuzdesi}%`,
                                      backgroundColor: '#2196F3'
                                    }}
                                  />
                                </div>
                                <span className={styles.statPercentage}>{birim.detaylar.q2Yuzdesi}%</span>
                              </div>
                            </div>
                          </div>
                        </div>

                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className={styles.wrapper}>
      <PageHeader 
        title="Yayınlar-Birimler"
        description="Akademik yayın performansı ve istatistikler"
        breadcrumbs={[
          { text: 'Ana Sayfa', href: '/' },
          { text: 'Yayınlar', href: '/yayinlar' },
          { text: 'Yayınlar-Birimler' }
        ]}
      />

      <FilterCard>
        <FilterCard.Row>
          <FilterCard.Col>
            <FilterCard.SearchInput
              placeholder="Birim adı ile ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </FilterCard.Col>
        </FilterCard.Row>
      </FilterCard>
        
      <div className="card border-0 shadow-sm publications-list">
        <div className="card-body">
          {renderTableContent()}
        </div>
      </div>
    </div>
  );
};

export default YayinlarBirimler; 